import { Injectable } from '@angular/core';
import { AssetUIModel } from '@thehubz/client/core';
import { AnyType, AssetType, Metadata } from '@thehubz/shared/model';
import { isArray } from 'class-validator';

@Injectable({
  providedIn: 'root',
})
export class JackpotAssetsFilterService {
  applySingleFilter(filterType: string, filterKey: string, assets: AssetUIModel[], searchText?: string) {
    let filteredAssets: AssetUIModel[] = [];
    if (filterType !== 'assetType') {
      switch (filterKey) {
        case 'sortByLastCreated': {
          filteredAssets = [...assets].sort(function (a, b) {
            const c = new Date(a.createdAt).getTime();
            const d = new Date(b.createdAt).getTime();
            return d - c;
          });
          break;
        }
        case 'sortByAlphabetically': {
          filteredAssets = [...assets].sort(function (a, b) {
            if (a.name < b.name) {
              return -1;
            }
            if (a.name > b.name) {
              return 1;
            }
            return 0;
          });
          break;
        }
        case 'search': {
          if (searchText) filteredAssets = assets.filter((asset) => asset.name.toLowerCase().includes(searchText.toLowerCase()));
          break;
        }
        default: {
          filteredAssets = assets;
          break;
        }
      }
    } else {
      filteredAssets = assets.filter((asset) => asset.assetType === filterKey);
    }
    return filteredAssets;
  }

  applyMultipleFilters(filterData: any, assets: AssetUIModel[]) {
    let filteredAssets: AssetUIModel[] = assets;
    let appliedFilters = 0;
    const filterKeys = Object.keys(filterData).filter((k) => filterData[k].length);

    for (const filterKey of filterKeys) {
      switch (filterKey) {
        case 'assetType': {
          if (isArray(filterData[filterKey])) {
            appliedFilters++;
            filteredAssets = filteredAssets.filter((asset) => {
              return filterData[filterKey].some((type: string) => {
                return type === asset.assetType;
              });
            });
          }
          continue;
        }
        case 'formatType': {
          if (isArray(filterData[filterKey])) {
            appliedFilters++;
            filteredAssets = filteredAssets.filter((asset) => {
              return filterData[filterKey].some((format: string) => {
                return asset['metadata'].ext === format;
              });
            });
          }
          continue;
        }
        case 'duration_from': {
          appliedFilters++;
          filteredAssets = filteredAssets.filter((asset) => parseInt(asset['metadata'].duration) >= this.convertTimeInSeconds(filterData[filterKey]));
          continue;
        }
        case 'duration_to': {
          appliedFilters++;
          filteredAssets = filteredAssets.filter((asset) => parseInt(asset['metadata'].duration) <= this.convertTimeInSeconds(filterData[filterKey]));
          continue;
        }
        case 'mediumType': {
          if (isArray(filterData[filterKey])) {
            appliedFilters++;
            filteredAssets = filteredAssets.filter((asset) => {
              return filterData[filterKey].some((medium: string) => {
                return medium === asset['metadata']?.assetMedium;
              });
            });
          }
          continue;
        }
      }
    }
    return { filteredAssets: filteredAssets, appliedFilters: appliedFilters };
  }

  getMetaData(dataType: string, asset: AssetUIModel) {
    const version = asset.currentVersion;
    const defaultRatio = version?.aspectRatios?.find((ar) => ar.aspectRatio === version.defaultAspectRatio);
    const mainFile = defaultRatio?.formats?.find((f) => f.fileNameRef === defaultRatio.defaultFileFormat);
    const metadata = mainFile?.metadata || {};
    const formats = asset.currentVersion?.aspectRatios
      ?.flatMap((aspectRatio) => aspectRatio.formats)
      ?.filter((format) => {
        return format?.fileNameRef && !format.fileNameRef.startsWith('gen') && format.fileNameRef !== 'main';
      });

    let data;
    switch (dataType) {
      case 'format': {
        data = Metadata(metadata).getWidth() + 'x' + Metadata(metadata).getHeight();
        break;
      }
      case 'duration': {
        data = asset.customFields?.['exactDuration'] ?? Metadata(metadata).getDuration();
        break;
      }
      case 'formats': {
        data = formats;
        break;
      }
      case 'aspectRatio': {
        data = Metadata(metadata).getAspectRatio();
        break;
      }
    }

    return data;
  }

  convertTimeInSeconds(timeString: string): number {
    const parts = timeString.split(':');
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = parseInt(parts[2]);
    return hours * 3600 + minutes * 60 + seconds;
  }

  splitAssets(assets: AssetUIModel[]) {
    const splitedAssets: any[] = [];
    assets.forEach((asset: AssetUIModel) => {
      const formats = this.getMetaData('formats', asset);
      if (formats) {
        formats.forEach((format: any) => {
          const newAsset = { ...asset };
          const duration = this.getMetaData('duration', asset);
          newAsset.name = format.name;
          newAsset['metadata'] = {
            assetMedium: format.assetMedium,
            duration: parseFloat(duration).toFixed(2),
            aspectRatio: format.metadata?.aspect_ratio,
            ext: format.ext,
          };
          newAsset.assetType = this.getAssetType(format.ext, format.mimetype);
          splitedAssets.push(newAsset);
        });
      }
    });
    return splitedAssets;
  }

  getAssetType(ext: string, mimetype: string) {
    let assetTypeName: AnyType = '';
    const assetType = Object.values(AssetType).find((obj) => {
      if (ext === 'ogg') {
        return obj.mimeTypes.map((type) => type.ext).includes(ext) && obj.mimeTypes.map((type) => type.mimeType).includes(mimetype);
      } else {
        return obj.mimeTypes.map((type) => type.ext).includes(ext);
      }
    });
    if (assetType?.name) {
      assetTypeName = assetType.name;
    } else {
      assetTypeName = 'Other';
    }
    return assetTypeName;
  }
}
